import type { PackageLiabilityReport } from '@salonomy/types';
import { Card, Pill, Stat } from '@salonomy/ui';
import { usePackageLiabilityReport } from '../../data/packages.js';
import { money, moneyWhole } from '../../lib/format.js';

/**
 * WS-13 Feature 3a — the package liability reconciliation card (manager-only API), mirroring the
 * gift-card LiabilityCard in features/reports/VouchersAdminPanel. Surfaces sold / redeemed / expired /
 * outstanding, the SERVER's recognised revenue (NOT the sold total), and PROMINENTLY the `reconciles`
 * invariant — a clear warning if the ledger doesn't net to the penny.
 *
 * HONESTY (ADR-0009): a sold package is a LIABILITY, never revenue. The only "revenue" surfaced is the
 * report's `revenueRecognisedPence` (recognised at redemption) — NEVER the sold total. Every figure is
 * the server's; nothing is recomputed here. Gated on `enabled` so it never fires for a non-manager (the
 * read is manager-only on the API).
 */
export function PackageLiabilityPanel({ enabled = true }: { enabled?: boolean }) {
  const { data: report, isLoading, isError } = usePackageLiabilityReport(enabled);

  return (
    <Card>
      <div className="section-title" style={{ marginBottom: 4 }}>
        Package liability
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
        A sold prepaid package is a liability, not revenue. Revenue is recognised only as sessions
        are redeemed — the figures below are the server&rsquo;s, reconciled against the ledger.
      </div>

      {isError ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Couldn&rsquo;t load the liability report.
        </div>
      ) : isLoading || !report ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Loading liability report…
        </div>
      ) : (
        <LiabilityBody report={report} />
      )}
    </Card>
  );
}

function LiabilityBody({ report }: { report: PackageLiabilityReport }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* PROMINENT reconciliation status — a false reconciles is a money-integrity alarm. */}
      {report.reconciles ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--slm-success-bg, #ecfdf3)',
            fontSize: 13,
          }}
        >
          <Pill tone="success">Reconciles</Pill>
          <span className="muted">
            Sold − redeemed − expired equals the outstanding liability, to the penny.
          </span>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--slm-danger-bg, #fef3f2)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Pill tone="danger">Does not reconcile</Pill>
          <span>
            The liability ledger does not net to the penny. Do not rely on these figures — raise
            this with support before reporting.
          </span>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 16,
        }}
      >
        <Stat label="Sold (liability)" value={moneyWhole(report.soldPence)} />
        <Stat label="Redeemed" value={moneyWhole(report.redeemedPence)} />
        <Stat label="Expired / void" value={moneyWhole(report.expiredPence)} />
        <Stat label="Outstanding liability" value={moneyWhole(report.outstandingPence)} />
      </div>

      <div className="settings-list">
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Recognised revenue</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Revenue recognised at redemption (ex-VAT) — not the sold total.
            </div>
          </div>
          <Stat label="Recognised" value={money(report.revenueRecognisedPence)} />
        </div>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Output VAT</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              VAT due on the recognised redemption revenue.
            </div>
          </div>
          <Stat label="VAT" value={money(report.vatOutputPence)} />
        </div>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Active packages</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Outstanding instruments still redeemable.
            </div>
          </div>
          <Pill tone="neutral">{report.activePackageCount}</Pill>
        </div>
      </div>
    </div>
  );
}
